import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isFirebaseConfigured } from '@/firebase';
import { GUEST_DAILY_LIMIT, USER_DAILY_LIMIT } from '@/constants/limits';
import { trackEvent } from '@/services/analyticsService';
import {
  callGenerateSnapshots,
  callProcessMedia,
  functionsErrorMessage,
} from '@/services/functionsService';
import { useAuthStore } from '@/store/authStore';
import { useDownloadStore } from '@/store/downloadStore';
import { useSettingsStore } from '@/store/settingsStore';
import { toast, useUiStore } from '@/store/uiStore';
import { generateId, showBrowserNotification } from '@/utils/misc';
import { detectPlatformFromUrl } from '@/utils/url';
import type { DownloadRecord, ProcessMediaInput } from '@/types';

function startOfTodayMs(): number {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function buildOptimisticRecord(input: ProcessMediaInput): DownloadRecord {
  return {
    id: `local-${generateId()}`,
    uid: 'local',
    sourceUrl: input.url,
    platform: detectPlatformFromUrl(input.url) ?? 'instagram',
    outputType: input.outputType,
    status: 'processing',
    progress: 8,
    quality: input.quality ?? null,
    fileSize: null,
    durationSec: null,
    downloadUrl: null,
    metadata: null,
    error: null,
    createdAt: Date.now(),
    completedAt: null,
    expiresAt: null,
  };
}

export function useUrlProcessor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ProcessMediaInput) => {
      if (!isFirebaseConfigured) {
        throw new Error('Firebase is not configured yet — copy .env.example to .env and add your keys.');
      }

      // Courtesy pre-check; Cloud Functions enforce quotas authoritatively.
      // Signed-in users are unlimited — only guests are capped.
      const { user } = useAuthStore.getState();
      if (!user) {
        const todayCount = useDownloadStore
          .getState()
          .guestHistory.filter((record) => record.createdAt >= startOfTodayMs()).length;
        if (todayCount >= GUEST_DAILY_LIMIT) {
          throw new Error(
            `Guest limit of ${GUEST_DAILY_LIMIT} downloads/day reached. Sign in free for ${USER_DAILY_LIMIT} downloads/day.`
          );
        }
        input = { ...input, guestId: useSettingsStore.getState().guestId };
      }

      trackEvent('download_started', {
        platform: detectPlatformFromUrl(input.url) ?? 'unknown',
        output: input.outputType,
        url: input.url,
      });
      return input.outputType === 'snapshots' ? callGenerateSnapshots(input) : callProcessMedia(input);
    },

    onMutate: (input) => {
      useUiStore.getState().setProcessingActive(true);
      const optimistic = buildOptimisticRecord(input);
      useDownloadStore.getState().upsertQueueItem(optimistic);
      return { tempId: optimistic.id };
    },

    onSuccess: (result, _input, context) => {
      const { record } = result;
      const store = useDownloadStore.getState();
      if (context) store.removeQueueItem(context.tempId);

      if (!useAuthStore.getState().user) {
        store.addGuestRecord(record);
      }

      // Pop the "ready" modal so the user can download immediately without
      // scrolling to the history list (matters most on mobile). Fall back to a
      // toast if for some reason there's no direct link.
      if (record.downloadUrl) {
        useUiStore.getState().openDownloadReady(record);
      } else {
        toast({
          title: 'Media ready to download!',
          description: record.metadata?.title ?? record.sourceUrl,
          variant: 'success',
        });
      }
      if (useSettingsStore.getState().notificationsEnabled) {
        showBrowserNotification('Download ready', record.metadata?.title ?? 'Your media has finished processing.');
      }
      trackEvent('download_completed', {
        platform: record.platform,
        output: record.outputType,
        url: record.sourceUrl,
        quality: record.quality ?? 'n/a',
      });

      queryClient.invalidateQueries({ queryKey: ['history'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },

    onError: (error, input, context) => {
      if (context) useDownloadStore.getState().removeQueueItem(context.tempId);
      const message =
        error instanceof Error && !('code' in error) ? error.message : functionsErrorMessage(error);
      trackEvent('download_failed', {
        platform: detectPlatformFromUrl(input.url) ?? 'unknown',
        output: input.outputType,
        url: input.url,
        error: message,
      });
      toast({
        title: 'Processing failed',
        description: message,
        variant: 'error',
      });
    },

    onSettled: () => {
      useUiStore.getState().setProcessingActive(false);
    },
  });
}
